import {
    Button,
    DropDown,
    DropdownFilterButton,
    getFilterElementsDefault,
    getMaskedPhone,
    getMaskFromName,
    Input,
    NoData,
    Pending,
    sharpDateFormatter,
    useActionsBtnContext,
    useErrorState,
    usePendingState,
    useWorkplaceState
} from '@finbeat/react-toolkit';
import { faEdit, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { getBanksList } from 'axios/bankApi';
import { getSelectList } from 'axios/dictionaryApi';
import {
    changeFundsTransfer,
    deleteFundsTransferSetting,
    downloadFile,
    getFundsTransferExcel,
    getFundsTransferList,
    getFundsTransferSettings
} from 'axios/fundsTransferApi';
import { BOWorkplace } from 'base-components/BOWorkplace/BOWorkplace';
import DataTimeText from 'components/Common/DataTime';
import { subDays } from 'date-fns';
import FileSaver from 'file-saver';
import useApi from 'hooks/useApi';
import useAuth from 'hooks/useAuth';
import { Bank } from 'models/api/bank';
import {
    FundsTransferChange,
    FundsTransferFilter,
    FundsTransferTable,
    UnloadSetting
} from 'models/api/fundsTransfer';
import { useGeneralSettingsContext } from 'providers/GeneralSettingsProvider';
import { useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, List, Row } from 'reactstrap';
import { getEndOfTheDay, getStartOfTheDay } from 'utils/dataTime';
import './FundsTransfers.scss';
import FundsTransfersFilter from './FundsTransfersFilter';
import ModalFundsTransferSetting from './ModalFundsTransferSetting';
import ModalUnloadRegistry from './ModalUnloadRegistry';

const FundsTransfers = () => {
    const { hasRole } = useAuth();
    const { t, i18n } = useTranslation();
    const api = useApi();
    const queryClient = useQueryClient();
    const { dateFormat, dateTimeFormat } = useGeneralSettingsContext();
    const [fundsTransferSettings, setFundsTransferSettings] = useState<UnloadSetting[]>([]);
    const currentSetting = useRef<UnloadSetting>();
    const [isModalShown, setIsModalShown] = useState(false);
    const [isUnloadModalShown, setIsUnloadModalShown] = useState(false);
    const { addRequest } = useActionsBtnContext();
    const { handleRequestError, handleError } = useErrorState();
    const { setPending } = usePendingState();

    const { isError, isFetching, isFetched, refetch } = useQuery(
        ['fund-transfer-settings', i18n.language],
        () => getFundsTransferSettings(api),
        {
            onSuccess: (data) => {
                data && setFundsTransferSettings(data);
            },
        }
    );

    const [bankGroups, setBankGroups] = useState<DropDown[]>([]);
    const [clientTypes, setClientTypes] = useState<DropDown[]>([]);
    const { isFetching: dictLoading } = useQuery(
        'orderSettingBasketType',
        () => getSelectList(api, ['BankGroups', 'ClientTypes'], i18n.language),
        {
            onSuccess: (data) => {
                data['BankGroups'] && setBankGroups(data['BankGroups']);
                data['ClientTypes'] && setClientTypes(data['ClientTypes']);
            },
            enabled: isModalShown,
        }
    );

    const [brokers, setBanks] = useState<Bank[]>([]);
    const { isFetching: brokersLoading } = useQuery(
        ['banks', i18n.language],
        () => getBanksList(api),
        {
            onSuccess: (banks) => {
                const brokers = banks.filter((b) => b.isBroker);
                setBanks(brokers);
            },
            onError: handleRequestError,
            enabled: isModalShown && !brokers.length,
        }
    );

    const updateFundsTransfer = useMutation(
        'updateFundsTransfer',
        async (item: FundsTransferChange) => {
            setPending(true);
            const result = await changeFundsTransfer(api, item);
            setPending(false);
            return result;
        },
        {
            onError: () => handleError(),
        }
    );

    const rootClassName = 'funds-transfers';
    const className = `${rootClassName}-settings`;
    return (
        <>
            <Row className={rootClassName}>
                <Col xs="4">
                    <div className={className + ' custom-scrollbar pl-3'}>
                        {isFetching ? (
                            <Pending />
                        ) : isError ? (
                            <div className="text-center p-3">
                                <Trans i18nKey="BackOfficeCantLoadFundsTransferSettings" />
                            </div>
                        ) : isFetched ? (
                            fundsTransferSettings.map((setting, index) => (
                                <Card className="mb-3" key={setting.id}>
                                    <CardHeader>
                                        <Row>
                                            <Col xs="9">
                                                <div className={`${className}__header`}>
                                                    <h4>
                                                        <Trans i18nKey="BackOfficeUnloadSetting" />{' '}
                                                        #{setting.id}
                                                    </h4>
                                                </div>
                                            </Col>
                                            <Col xs="3" className="py-2">
                                                {hasRole('fundsTransfersSettingsUpdate') && (
                                                    <FontAwesomeIcon
                                                        icon={faEdit}
                                                        className={className + '__header-button'}
                                                        role="button"
                                                        onClick={() => {
                                                            currentSetting.current = setting;
                                                            setIsModalShown(true);
                                                        }}
                                                    />
                                                )}
                                                {hasRole('fundsTransfersSettingsDelete') && (
                                                    <FontAwesomeIcon
                                                        icon={faTrash}
                                                        className={className + '__header-button'}
                                                        role="button"
                                                        onClick={() => {
                                                            addRequest({
                                                                content: t('BackOfficeAreYouSure'),
                                                                confirmCallback: (res) => {
                                                                    if (
                                                                        res &&
                                                                        setting.id !== null
                                                                    ) {
                                                                        deleteFundsTransferSetting(
                                                                            api,
                                                                            setting.id
                                                                        ).then(() => {
                                                                            refetch();
                                                                        });
                                                                    }
                                                                },
                                                            });
                                                        }}
                                                    />
                                                )}
                                            </Col>
                                        </Row>
                                    </CardHeader>
                                    <CardBody className={`${className}__body`}>
                                        <dl>
                                            <dt>
                                                <Trans i18nKey="BackOfficeName" />
                                            </dt>
                                            <dd>{setting.name}</dd>
                                            <dt>
                                                <Trans i18nKey="BackOfficeClientAccountBank" />
                                            </dt>
                                            <dd>
                                                {setting.clientBankGroupName || (
                                                    <Trans i18nKey="BackOfficeOtherBanks" />
                                                )}
                                            </dd>
                                            <dt>
                                                <Trans i18nKey="BackOfficeFundsInTheBank" />
                                            </dt>
                                            <dd>{setting.brokerBankName}</dd>
                                            <dt>
                                                <Trans i18nKey="BackOfficeUnloadSettingEnabled" />
                                            </dt>
                                            <dd>
                                                {setting.isActive ? (
                                                    <Trans i18nKey="BackOfficeRun" />
                                                ) : (
                                                    <Trans i18nKey="BackOfficeDoNotRun" />
                                                )}
                                            </dd>
                                            <dt>
                                                <Trans i18nKey="BackOfficeClientTypeFilter" />
                                            </dt>
                                            <dd>
                                                {setting.clientTypeName || (
                                                    <Trans i18nKey="BackOfficeAll" />
                                                )}
                                            </dd>
                                            <dt>
                                                <Trans i18nKey="BackOfficePeriods" />
                                            </dt>
                                            <dd>
                                                {!setting.periods.length ? (
                                                    <Trans i18nKey="BackOfficeDefault" />
                                                ) : (
                                                    setting.periods.map((p, i) => (
                                                        <div key={i}>
                                                            {sharpDateFormatter(p.sd, dateFormat)}
                                                            <span> - </span>
                                                            {sharpDateFormatter(p.ed, dateFormat)}
                                                            <br />
                                                            <dl className="mx-3">
                                                                <dt>
                                                                    <Trans i18nKey="BackOfficeUnloadSchedule" />
                                                                </dt>
                                                                <dd>
                                                                    {!p.schedule.length ? (
                                                                        <Trans i18nKey="BackOfficeUnloadScheduleNotSpecified" />
                                                                    ) : (
                                                                        p.schedule.map((s, i) => (
                                                                            <div key={i}>
                                                                                {s.time.slice(0, 5)}
                                                                                <br />
                                                                            </div>
                                                                        ))
                                                                    )}
                                                                </dd>
                                                            </dl>
                                                        </div>
                                                    ))
                                                )}
                                            </dd>
                                            <dt>
                                                <Trans i18nKey="BackOfficeOrders" />
                                            </dt>
                                            <dd>
                                                {setting.orders.length === 0 ? (
                                                    <Trans i18nKey="BackOfficeNo" />
                                                ) : (
                                                    setting.orders.map((o) => o.orderId).join(', ')
                                                )}
                                            </dd>
                                        </dl>
                                    </CardBody>
                                </Card>
                            ))
                        ) : (
                            <NoData />
                        )}
                        {hasRole('fundsTransfersSettingsCreate') && (
                            <Button
                                className="w-100"
                                color="secondary"
                                onClick={() => setIsModalShown(true)}
                            >
                                <FontAwesomeIcon icon={faPlus} /> <Trans i18nKey="BackOfficeAdd" />
                            </Button>
                        )}
                    </div>
                </Col>
                <Col xs="8">
                    <BOWorkplace<FundsTransferTable, FundsTransferTable, FundsTransferFilter>
                        pageTitle="BackOfficeUploadingRegisterForTransferOfLoanAmountsToClient"
                        filterParams={{
                            sd: getStartOfTheDay(subDays(new Date(), 7)),
                            ed: getEndOfTheDay(),
                        }}
                        initColumns={[
                            {
                                key: 'id',
                                name: <Trans i18nKey="BackOfficeId" />,
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                width: 60,
                                minWidth: 60,
                            },
                            {
                                key: 'bankName',
                                name: <Trans i18nKey="BackOfficeBankName" />,
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                formatter: ({ row }) => (
                                    <List className={rootClassName + '__table-list'}>
                                        <li>{row.bankName}</li>
                                        <li>
                                            <Link
                                                to={`/fundstransferdetails?registryId=${row.id}`}
                                                target="_blank"
                                            >
                                                {row.taskId}
                                            </Link>
                                        </li>
                                    </List>
                                ),
                            },
                            {
                                key: 'sd',
                                name: <Trans i18nKey="BackOfficeStartDate" />,
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                formatter: ({ row }) => <DataTimeText value={row.sd} />,
                            },
                            {
                                key: 'ed',
                                name: <Trans i18nKey="BackOfficeEndDate" />,
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                formatter: ({ row }) => <DataTimeText value={row.ed} />,
                            },
                            {
                                key: 'numberOfTransfers',
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                name: <Trans i18nKey="BackOfficeNumberOfTransfers" />,
                                width: 60,
                                minWidth: 60,
                            },
                            {
                                key: 'unloadSetting',
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                name: <Trans i18nKey="BackOfficeUnloadSetting" />,
                            },
                            {
                                key: 'unloadType',
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                name: <Trans i18nKey="BackOfficeUnloadType" />,
                            },
                            {
                                key: 'isSentToBank',
                                name: '',
                                width: 60,
                                minWidth: 60,
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                formatter: ({ row, onRowChange }) => (
                                    <div className="p-3">
                                        <Input
                                            type="checkbox"
                                            checked={!!row.isSentToBank}
                                            onChange={async (e) => {
                                                onRowChange({
                                                    ...row,
                                                    isSentToBank: e.target.checked,
                                                });
                                                updateFundsTransfer.mutate({
                                                    id: row.id,
                                                    isSentToBank: e.target.checked,
                                                });
                                            }}
                                        />
                                    </div>
                                ),
                            },
                            {
                                key: 'files',
                                name: <Trans i18nKey="BackOfficeFiles" />,
                                cellClass: 'base-data-grid__cell_centered',
                                headerCellClass: 'base-data-grid__cell_centered',
                                formatter: ({ row }) => {
                                    if (row.files) {
                                        return (
                                            <List className={rootClassName + '__table-list'}>
                                                {row.files.map((item, i) => (
                                                    <li key={i}>
                                                        <a
                                                            href={'fundstransfers/file/' + item[0]}
                                                            onClick={async (e) => {
                                                                e.preventDefault();
                                                                const file = await downloadFile(
                                                                    api,
                                                                    item[0]
                                                                );
                                                                file &&
                                                                    FileSaver.saveAs(
                                                                        new Blob([file]),
                                                                        item[1].endsWith('xls')
                                                                            ? item[1] + 'x'
                                                                            : item[1]
                                                                    );
                                                            }}
                                                        >
                                                            {item[1].endsWith('xls')
                                                                ? item[1] + 'x'
                                                                : item[1]}
                                                        </a>
                                                    </li>
                                                ))}
                                            </List>
                                        );
                                    } else {
                                        return <></>;
                                    }
                                },
                            },
                        ]}
                        buttons={
                            hasRole('fundsTransfersUnloadRegistry')
                                ? [
                                      <DropdownFilterButton
                                          key="1"
                                          onClick={() => {
                                              setIsUnloadModalShown(true);
                                          }}
                                          title="BackOfficeUnloadRegistry"
                                      />,
                                  ]
                                : []
                        }
                        storagePath="fundsTransfers/columns"
                        loadingFilter={isFetching}
                        getFilterElements={getFilterElementsDefault([
                            {
                                id: 'sd',
                                formatter: (val) =>
                                    `${t('BackOfficeDtFrom')}: ${sharpDateFormatter(
                                        val.toString(),
                                        dateTimeFormat
                                    )}`,
                            },
                            {
                                id: 'ed',
                                formatter: (val) =>
                                    `${t('BackOfficeTo')}: ${sharpDateFormatter(
                                        val.toString(),
                                        dateTimeFormat
                                    )}`,
                            },
                            {
                                id: 'orderId',
                                name: t('BackOfficeAppId'),
                            },
                            {
                                id: 'phone',
                                name: t('BackOfficePhone'),
                                formatter: (val) =>
                                    getMaskedPhone(val.toString(), getMaskFromName('Uzbekistan')),
                            },
                            {
                                id: 'unloadSettingId',
                                name: t('BackOfficeUnloadSetting'),
                                formatter: (val) =>
                                    fundsTransferSettings.find(
                                        (t) => t.id?.toString() === val?.toString()
                                    )?.name || `${val}`,
                            },
                            {
                                id: 'unloadTypeCode',
                                name: t('BackOfficeUnloadType'),
                                formatter: (val) =>
                                    `${
                                        val?.toString() === '1'
                                            ? t('DictItemUnloadTypesManual')
                                            : t('DictItemUnloadTypesAutomatic')
                                    }`,
                            },
                        ])}
                        filter={
                            <FundsTransfersFilter
                                unloadSettings={fundsTransferSettings.map((s) => ({
                                    label: s.name,
                                    value: s.id || -1,
                                }))}
                            />
                        }
                        fetchQueryFn={(filter) => getFundsTransferList(api, filter)}
                        queryKey={['fundsTransfers']}
                        queryClient={queryClient}
                        fetchExcelQueryFn={(filter) => getFundsTransferExcel(api, filter)}
                        lineHeight={120}
                        showExcelBtn={hasRole('fundsTransfersExcel')}
                    />
                </Col>
            </Row>
            {isModalShown && (
                <ModalFundsTransferSetting
                    toggle={() => {
                        setIsModalShown(false);
                        currentSetting.current = undefined;
                    }}
                    updateList={refetch}
                    setting={currentSetting.current}
                    dictLoading={dictLoading}
                    brokersLoading={brokersLoading}
                    bankGroups={bankGroups}
                    brokers={brokers}
                    clientTypes={clientTypes}
                />
            )}
            {isUnloadModalShown && (
                <ModalUnloadRegistry
                    settings={fundsTransferSettings}
                    toggle={() => {
                        setIsUnloadModalShown(false);
                    }}
                />
            )}
        </>
    );
};

const useFundsTransfersWorkplaceState = () =>
    useWorkplaceState<FundsTransferTable, FundsTransferTable>();

export default FundsTransfers;
export { useFundsTransfersWorkplaceState };
